/* eslint-disable no-nested-ternary */
import _ from 'lodash';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

// -----------------LIBRARIES IMPORTS------------------- //
import {
	Button,
	DataTable,
	Input,
	ModalConfirmation,
	Slider,
	Typography,
} from 'design_system/src';

// -----------------DATA IMPORTS------------------- //

// -----------------STATIC IMPORTS------------------- //
import { ReactComponent as Bank } from 'design_system/src/static/icons/bank.svg';
import { ReactComponent as Check } from 'design_system/src/static/icons/checkmark.svg';
import { ReactComponent as Clock } from 'design_system/src/static/icons/clock.svg';
import { ReactComponent as CloseCircle } from 'design_system/src/static/icons/close-circle.svg';
import { ReactComponent as Card } from 'design_system/src/static/icons/credit-card.svg';
import { ReactComponent as Edit } from 'design_system/src/static/icons/edit-text.svg';
import { ReactComponent as List } from 'design_system/src/static/icons/list.svg';
import { ReactComponent as Mail } from 'design_system/src/static/icons/mail.svg';
import { ReactComponent as Money } from 'design_system/src/static/icons/money.svg';
import { ReactComponent as Motorcycle } from 'design_system/src/static/icons/motorcycle.svg';
import { ReactComponent as Calculator } from 'design_system/src/static/icons/calculator.svg';
import '../../../ozoner.scss';

// -----------------COMPONENTS IMPORTS------------------- //
import { Grid } from '@material-ui/core';
import {
	assignVehicle,
	calculateAndSaveOffers,
	calculateAndSaveOffersForCreditLine,
	updateCreditLineFinancialForm,
	updateFinancialForm,
} from 'models/financial-form/controllers/financial-form.controller';
import {
	FinancialFormTypeEnum,
	FormStatus,
	VehiclePurposeEnum,
	VehiclePurposeValues,
} from 'models/financial-form/enums/financial-form.enums';
import {
	calculateOffersForCreditLine,
	calculateOffersFromFinancialForm,
	CreditLineOffer,
	CreditLineOfferResult,
	LoanOffer,
	LoanOfferResult,
} from 'models/loan-offer/controllers/loan-offer.controller';
import {
	cancelPaymentLink,
	createPaymentLink,
	sendPaymentLinkEmail,
} from 'models/openpay/controllers/openpay.controller';
import {
	PaymentLinkType,
	PaymentMethodEnum,
} from 'models/openpay/enums/openpay.enums';
import {
	getPaymentLinkStatusDisplay,
	isPaymentLinkAwaiting,
	isPaymentLinkCompleted,
	PaymentLink,
	PaymentLinkMethodsValues,
	PaymentLinkTypeValues,
} from 'models/openpay/interfaces/openpay.interface';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { momentLocal } from 'models/shared/helpers/dates';
import { commonErrorHandler } from 'models/shared/helpers/errors';
import {
	priceFormatFinancial,
	priceMXNTotal,
} from 'models/shared/helpers/prices';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import { getSkus } from 'models/sku/controllers/sku.controller';
import { Sku, SkuStatusEnum } from 'models/sku/interfaces/sku.interface';
import { variantLabel } from 'models/sku-variant/helpers/skuVariantLabel';
import { SkuVariant } from 'models/sku-variant/interfaces/sku-variant.interface';
import { WhitelistNameEnum } from 'models/whitelist/interfaces/whitelist.interface';
import useProviders from 'hooks/useProviders';
import SkuSelector from 'views/sku/SkuSelector';
import {
	validationItem,
	validationItemState,
} from 'components/hocs/messages/messages';
import { ozonerContext } from '../../OzonerContext';
import { useValidOffer } from './useValidOffer';

function buildOfferTable(offer: CreditLineOffer | LoanOffer) {
	return [
		{ label: 'Semanas', value: offer.duration, valueColor: 'primary_300' },
		{
			label: 'Pago semanal (con Seguro y GPS)',
			value: priceMXNTotal(offer.periodicPayment),
			valueColor: 'primary_300',
		},
		{
			label: 'Tasa semanal',
			value: `${(offer.periodicRate * 100).toFixed(2)}%`,
		},
		{
			label: 'Tasa con IVA',
			value: `${(offer.periodicRateWithTax * 100).toFixed(2)}%`,
		},
		{ label: 'Ticket de la moto', value: priceMXNTotal(offer.ticketPrice) },
		{ label: 'Enganche', value: priceMXNTotal(offer.downPaymentAmount) },
		{
			label: 'Monto a financiar',
			value: priceMXNTotal(offer.financingAmount),
		},
		{
			label: 'Pago total del seguro',
			value: priceMXNTotal(offer.totalInsuranceAmount),
		},
		{
			label: 'Pago total del GPS',
			value: priceMXNTotal(offer.totalGpsAmount),
		},
		{
			label: 'Pago semanal extras (Seguro + GPS)',
			value: priceMXNTotal(offer.periodicExtraPayment),
		},
	];
}

export function OfferCalculator() {
	const { financialForm, paymentLinks, updateFormData } =
		useContext(ozonerContext);

	const form = financialForm?.form as any;
	const isCreditLine = form?.type === FinancialFormTypeEnum.creditLine;
	const selectedSkuId = form?.sku as string | undefined;
	const selectedSkuVariantId = form?.skuVariant as string | undefined;
	const savedProviderId = form?.provider as string | undefined;
	const savedDownPayment: number | undefined = isCreditLine
		? form?.downPayment
		: form?.loanApplication?.downPayment;
	const savedLoanDuration: number | undefined = isCreditLine
		? form?.loanDuration
		: form?.loanApplication?.loanDuration;
	const savedVehiclePurpose = form?.vehiclePurpose as
		| VehiclePurposeEnum
		| undefined;

	const [openAdvance, setOpenAdvance] = useState(false);
	const [skus, setSkus] = useState<Sku[]>([]);
	const providers = useProviders();

	const [openSkuModal, setOpenSkuModal] = useState(false);
	const [openConfirmSave, setOpenConfirmSave] = useState(false);
	const [calcSkuId, setCalcSkuId] = useState<string | undefined>(undefined);
	const [calcSkuVariantId, setCalcSkuVariantId] = useState<string | undefined>(
		undefined,
	);

	const [downPaymentInput, setDownPaymentInput] = useState<number | undefined>(
		undefined,
	);
	const [sliderValue, setSliderValue] = useState(0);
	const [selectedDuration, setSelectedDuration] = useState<
		number | undefined
	>();
	const [offerResult, setOfferResult] = useState<
		LoanOfferResult | CreditLineOfferResult | null
	>(null);
	const [calcVehiclePurpose, setCalcVehiclePurpose] = useState<
		VehiclePurposeEnum | undefined
	>(undefined);
	const [calculating, setCalculating] = useState(false);
	const [saving, setSaving] = useState(false);

	const [currentLink, setCurrentLink] = useState<PaymentLink | undefined>();

	const selectedDurationRef = useRef<number | undefined>(undefined);
	useEffect(() => {
		selectedDurationRef.current = selectedDuration;
	}, [selectedDuration]);

	const calcVehiclePurposeRef = useRef<VehiclePurposeEnum | undefined>(
		undefined,
	);
	useEffect(() => {
		calcVehiclePurposeRef.current = calcVehiclePurpose;
	}, [calcVehiclePurpose]);

	useEffect(() => {
		getSkus({
			status: SkuStatusEnum.ACTIVE,
			requirePricedVariant: true,
			limit: -1,
		})
			.then((result) => setSkus(result.data))
			.catch(() => {});
	}, []);

	useEffect(() => {
		setCurrentLink(
			paymentLinks.find(
				(ele) =>
					ele.paymentLinkType === PaymentLinkType.DownPayment &&
					(isPaymentLinkAwaiting(ele.status) ||
						isPaymentLinkCompleted(ele.status)),
			),
		);
	}, [paymentLinks]);

	const calcSku = skus.find((s) => s._id === calcSkuId);
	const calcSkuVariant = calcSku?.variants?.find(
		(v) => v._id === calcSkuVariantId,
	);
	const ticketPrice = calcSkuVariant?.price || calcSku?.minPrice || 0;

	const providerValue =
		calcSkuVariant?.provider ?? savedProviderId ?? undefined;

	const fetchOffers = (ticketPriceParam: number, downPaymentAmount: number) => {
		if (!financialForm?.form) return;
		setCalculating(true);
		const calculate = isCreditLine
			? calculateOffersForCreditLine
			: calculateOffersFromFinancialForm;
		calculate(financialForm.form._id, {
			ticketPrice: ticketPriceParam,
			downPaymentAmount,
			vehiclePurpose: calcVehiclePurposeRef.current,
		})
			.then((result) => {
				setOfferResult(result);
				const offers = (result.status === 'approved' ? result.offers : []) as (
					| LoanOffer
					| CreditLineOffer
				)[];
				const stillValid = offers.find(
					(o) => o.duration === selectedDurationRef.current,
				);
				setSelectedDuration(
					stillValid ? selectedDurationRef.current : undefined,
				);
			})
			.catch(() => {
				setOfferResult(null);
				setSelectedDuration(undefined);
			})
			.finally(() => setCalculating(false));
	};

	const debouncedFetchOffers = useMemo(
		() => _.debounce(fetchOffers, 500),
		[isCreditLine, financialForm?.form?._id],
	);

	useEffect(() => () => debouncedFetchOffers.cancel(), [debouncedFetchOffers]);

	useEffect(() => {
		const canFetch =
			openAdvance && downPaymentInput !== undefined && !!calcSkuVariant;
		if (!canFetch) return;
		fetchOffers(ticketPrice, downPaymentInput);
	}, [calcVehiclePurpose]); // re-fetch when vehicle purpose changes

	const resetCalculatorState = () => {
		setOpenSkuModal(false);
		setOpenConfirmSave(false);
		setCalcSkuId(selectedSkuId);
		setCalcSkuVariantId(selectedSkuVariantId);
		setOfferResult(null);
		setSelectedDuration(undefined);
		setDownPaymentInput(undefined);
		setSliderValue(0);
		setCalcVehiclePurpose(undefined);
	};

	const openModal = () => {
		resetCalculatorState();
		setOpenAdvance(true);
		const initialDownPayment = savedDownPayment ?? 0;
		setDownPaymentInput(initialDownPayment);
		setSliderValue(initialDownPayment);
		setSelectedDuration(savedLoanDuration);
		selectedDurationRef.current = savedLoanDuration;
		setCalcVehiclePurpose(savedVehiclePurpose);
		calcVehiclePurposeRef.current = savedVehiclePurpose;
		const initialSku = skus.find((s) => s._id === selectedSkuId);
		const initialSkuVariant = initialSku?.variants?.find(
			(v) => v._id === selectedSkuVariantId,
		);
		if (initialSkuVariant) {
			const initialTicketPrice =
				initialSkuVariant.price || initialSku?.minPrice || 0;
			fetchOffers(initialTicketPrice, initialDownPayment);
		}
	};

	const closeModal = () => {
		setOpenAdvance(false);
		debouncedFetchOffers.cancel();
		resetCalculatorState();
	};

	const minDownPayment = offerResult?.minDownPaymentAmount ?? 0;
	const maxDownPayment = offerResult?.maxDownPaymentAmount;
	const hasDownPaymentBounds =
		offerResult !== null && maxDownPayment !== undefined;

	const handleDownPaymentChange = (e: any) => {
		if (e.target.value === '') {
			setDownPaymentInput(undefined);
			setSliderValue(0);
			setSelectedDuration(undefined);
			debouncedFetchOffers.cancel();
			setOfferResult(null);
			return;
		}
		const raw = parseFloat(e.target.value);
		if (Number.isNaN(raw)) return;
		setDownPaymentInput(raw);
		setSliderValue(raw);
		debouncedFetchOffers(ticketPrice, raw);
	};

	const handleSliderChange = (_e: any, value: number | number[]) => {
		const raw = value as number;
		setSliderValue(raw);
		setDownPaymentInput(raw);
		debouncedFetchOffers(ticketPrice, raw);
	};

	const handleSkuChange = (sku: Sku, skuVariant: SkuVariant) => {
		setCalcSkuId(sku._id);
		setCalcSkuVariantId(skuVariant._id);
		setOpenSkuModal(false);
		const newTicketPrice = skuVariant.price || sku.minPrice || 0;
		fetchOffers(newTicketPrice, downPaymentInput ?? 0);
	};

	const selectedOffer = (
		(offerResult?.status === 'approved' ? offerResult.offers : []) as (
			| LoanOffer
			| CreditLineOffer
		)[]
	).find((o) => o.duration === selectedDuration);

	const save = () => {
		if (
			!financialForm?.form ||
			!calcSkuId ||
			!calcSkuVariantId ||
			selectedDuration === undefined ||
			downPaymentInput === undefined ||
			!selectedOffer
		)
			return;
		setSaving(true);
		const dto: any = {
			sku: calcSkuId,
			skuVariant: calcSkuVariantId,
		};
		if (providerValue) dto.provider = providerValue;
		if (calcVehiclePurpose) dto.vehiclePurpose = calcVehiclePurpose;
		const updatePromise = isCreditLine
			? updateCreditLineFinancialForm(financialForm.form._id, {
					...dto,
					downPayment: downPaymentInput,
					loanDuration: selectedDuration,
			  })
			: updateFinancialForm(financialForm.form._id, {
					...dto,
					loanApplication: {
						downPayment: downPaymentInput,
						loanDuration: selectedDuration,
					},
			  });
		updatePromise
			.then((newForm) => {
				toasterNotification({
					msg: 'Datos de la oferta actualizados',
					style: 'light',
					toasterType: toasterOptions.success,
				});
				updateFormData(newForm);
				setOpenAdvance(false);
				resetCalculatorState();
			})
			.catch((e) => {
				toasterNotification({
					msg: e ?? 'Ocurrio un error',
					toasterType: toasterOptions.error,
					style: 'light',
				});
			})
			.finally(() => setSaving(false));
	};

	const reaches =
		!calculating &&
		offerResult?.status === 'approved' &&
		offerResult.offers.length > 0;

	return (
		<>
			<div
				id="offer-calculator"
				className="p_md border_b_solid border_1 border_neutral_400"
			>
				<div className="display_flex w_100_per flex_align_start">
					<div className="flex_grow_1">
						<Typography scale="medium" weight="400" textColor="neutral_800">
							Enganche
						</Typography>
						<Typography
							scale="large"
							weight="600"
							textColor="neutral_800"
							className=""
						>
							{savedDownPayment !== undefined
								? priceMXNTotal(savedDownPayment, 2)
								: '      -- MXN'}
						</Typography>
					</div>
					<div className="flex_grow_1">
						<Typography scale="medium" weight="400" textColor="neutral_800">
							Plazo
						</Typography>
						<Typography
							scale="large"
							weight="600"
							textColor="neutral_800"
							className=""
						>
							{savedLoanDuration !== undefined
								? `${savedLoanDuration} semanas`
								: '      -- semanas'}
						</Typography>
					</div>
				</div>
				<Button
					onClick={openModal}
					variant="outline"
					color="secondary"
					className="m_t_md w_100_per"
					icon={<Calculator />}
					scale="small"
				>
					Abrir calculadora
				</Button>
			</div>
			{openAdvance && (
				<ModalConfirmation
					openConfirmation={openAdvance}
					handleClose={closeModal}
					icon={<Calculator className="text_secondary_600" />}
					isConfirmationLoading={saving}
					widthDesktop={50}
					actions={[
						{
							label: 'Cancelar',
							color: 'grey',
							action: closeModal,
						},
						{
							label: 'Guardar',
							color: 'primary',
							disabled: saving || calculating || !selectedOffer,
							action: () => setOpenConfirmSave(true),
						},
					]}
				>
					<>
						<Typography scale="large" weight="600" className="m_b_md">
							Calculadora de ofertas
						</Typography>

						<div className="dso_input_cont">
							<div className="dso_input_title flex_gap_xs display_flex">
								<Motorcycle className="dso_input_img" />
								<div className="dso_input_text">SKU</div>
							</div>

							<div className="display_flex flex_justify_between flex_align_start flex_gap_sm m_b_sm">
								<div className="flex_grow_1">
									{calcSkuVariant ? (
										<div className="display_flex flex_gap_md">
											<div className="dim_50_px">
												<img
													className="w_100_per br_xs"
													src={calcSku?.images[0].url}
													alt=""
												/>
											</div>
											<div className="flex_grow_1">
												<Typography scale="small" weight="600">
													{calcSku?.code}
												</Typography>
												<Typography
													scale="xsmall"
													weight="400"
													textColor="neutral_600"
												>
													{variantLabel(calcSkuVariant, providers)}
												</Typography>
											</div>
										</div>
									) : (
										<Typography scale="small" weight="400" textColor="red_300">
											Debes seleccionar un SKU para calcular ofertas.
										</Typography>
									)}
								</div>
								<Button
									scale="small"
									variant="outline"
									color="secondary"
									onClick={() => setOpenSkuModal(true)}
								>
									Cambiar SKU
								</Button>
							</div>
						</div>

						{openSkuModal && (
							<ModalConfirmation
								openConfirmation={openSkuModal}
								handleClose={() => setOpenSkuModal(false)}
								widthDesktop={45}
								heightDesktop={80}
								actions={[
									{
										label: 'Cerrar',
										color: 'grey',
										action: () => setOpenSkuModal(false),
									},
								]}
							>
								<SkuSelector
									skus={skus}
									onClick={handleSkuChange}
									selectedSkuId={calcSkuId}
									selectedVariantId={calcSkuVariantId}
									loading={skus.length === 0}
								/>
							</ModalConfirmation>
						)}

						{!calcSkuVariant ? null : (
							<>
								<div className="dso_input_cont m_b_md">
									<div className="dso_input_title flex_gap_xs display_flex">
										<Motorcycle className="dso_input_img" />
										<div className="dso_input_text">Uso del vehículo</div>
									</div>
									<div className="display_flex flex_gap_sm m_t_xs">
										{Object.values(VehiclePurposeEnum).map((purpose) => (
											<Button
												key={purpose}
												scale="small"
												className="flex_grow_1"
												variant={
													calcVehiclePurpose === purpose
														? 'principal'
														: 'outline'
												}
												onClick={() => setCalcVehiclePurpose(purpose)}
											>
												{VehiclePurposeValues[purpose]}
											</Button>
										))}
									</div>
								</div>

								{!currentLink && (
									<div className="dso_input_cont">
										<div className="dso_input_title flex_gap_xs display_flex">
											<Money className="dso_input_img" />
											<div className="dso_input_text">Enganche</div>
										</div>

										<div className="display_flex flex_gap_md flex_align_end">
											{hasDownPaymentBounds &&
												(maxDownPayment ?? 0) > minDownPayment && (
													<div className="flex_grow_6">
														<Slider
															value={sliderValue}
															min={minDownPayment}
															max={maxDownPayment}
															step={50}
															name="downPaymentSlider"
															onChange={handleSliderChange}
														/>
														<div className="display_flex flex_justify_between m_b_sm">
															<Typography
																scale="xsmall"
																weight="400"
																textColor="neutral_600"
															>
																Mín. {priceMXNTotal(minDownPayment, 0)}
															</Typography>
															<Typography
																scale="xsmall"
																weight="400"
																textColor="neutral_600"
															>
																Máx. {priceMXNTotal(maxDownPayment ?? 0, 0)}
															</Typography>
														</div>
													</div>
												)}

											<div className="flex_grow_1">
												<Input
													type="number"
													onChange={handleDownPaymentChange}
													value={downPaymentInput}
													title=""
													name="downPayment"
													placeholder="Escribe el valor del enganche aquí"
													className="w_100_per"
												/>
											</div>
										</div>
									</div>
								)}

								{!calculating && (
									<div className="display_flex flex_col dso_input_cont flex_gap_sm m_t_md">
										<div className="dso_input_title flex_gap_xs display_flex flex_justify_between">
											<div className="display_flex">
												<List className="dso_input_img" />
												<div className="dso_input_text">Ofertas</div>
											</div>
											{offerResult && !calculating && (
												<span
													className={`br_sm p_x_sm flex_center ${
														reaches
															? 'bg_green_100 text_green_300'
															: 'bg_red_100 text_red_300'
													}`}
												>
													<Typography scale="xsmall" weight="600">
														{reaches ? 'Te alcanza' : 'Sin ofertas disponibles'}
													</Typography>
												</span>
											)}
										</div>

										<Grid container spacing={2}>
											{offerResult?.offers.map((offer) => (
												<Grid item xs={12} md={4} key={offer.duration}>
													<label htmlFor={`${offer.duration}-weeks`}>
														<div
															className={`p_sm br_xs shadow_soft text_left border_none display_flex flex_gap_sm ${
																selectedDuration === offer.duration
																	? 'bg_primary_25 border_solid border_1 border_primary_300'
																	: 'bg_neutral_0'
															}`}
														>
															<input
																id={`${offer.duration}-weeks`}
																type="checkbox"
																className="display_none"
																checked={selectedDuration === offer.duration}
																onChange={() =>
																	setSelectedDuration(
																		selectedDuration === offer.duration
																			? undefined
																			: offer.duration,
																	)
																}
															/>
															<div className="w_100_per">
																{buildOfferTable(offer).map((row) => (
																	<div
																		className="display_flex w_100_per"
																		key={row.label}
																	>
																		<Typography
																			scale="small"
																			weight="600"
																			textColor="neutral_700"
																			className="w_100_per break_word p_y_xxs p_x_xs"
																		>
																			{row.label}
																		</Typography>
																		<Typography
																			scale="small"
																			weight="600"
																			textColor={
																				(row.valueColor ?? 'neutral_800') as any
																			}
																			className="w_100_per break_word p_y_xxs p_x_xs"
																		>
																			{row.value}
																		</Typography>
																	</div>
																))}
															</div>
														</div>
													</label>
												</Grid>
											))}
										</Grid>
									</div>
								)}

								{openConfirmSave && (
									<ModalConfirmation
										openConfirmation={openConfirmSave}
										handleClose={() => setOpenConfirmSave(false)}
										icon={<Calculator className="text_primary_300" />}
										isConfirmationLoading={saving}
										widthDesktop={35}
										actions={[
											{
												label: 'Cancelar',
												color: 'grey',
												action: () => setOpenConfirmSave(false),
												disabled: saving,
											},
											{
												label: 'Guardar',
												color: 'primary',
												loading: saving,
												disabled: saving,
												action: save,
											},
										]}
									>
										<>
											<Typography scale="large" weight="600" className="m_b_md">
												¿Guardar estos datos en la solicitud?
											</Typography>
											<div className="display_flex flex_col flex_gap_xxs">
												<div className="display_flex flex_justify_between">
													<Typography
														scale="medium"
														weight="400"
														textColor="neutral_600"
													>
														Enganche seleccionado
													</Typography>
													<Typography scale="medium" weight="600">
														{downPaymentInput !== undefined
															? priceMXNTotal(downPaymentInput, 0)
															: '--'}
													</Typography>
												</div>
												<div className="display_flex flex_justify_between">
													<Typography
														scale="medium"
														weight="400"
														textColor="neutral_600"
													>
														Plazo seleccionado
													</Typography>
													<Typography scale="medium" weight="600">
														{selectedDuration !== undefined
															? `${selectedDuration} semanas`
															: '--'}
													</Typography>
												</div>
												<div className="display_flex flex_justify_between">
													<Typography
														scale="medium"
														weight="400"
														textColor="neutral_600"
													>
														Precio de la moto
													</Typography>
													<Typography scale="medium" weight="600">
														{priceMXNTotal(ticketPrice, 2)}
													</Typography>
												</div>
												<div className="display_flex flex_justify_between">
													<Typography
														scale="medium"
														weight="400"
														textColor="neutral_600"
													>
														Uso del vehículo
													</Typography>
													<Typography scale="medium" weight="600">
														{calcVehiclePurpose
															? VehiclePurposeValues[calcVehiclePurpose]
															: '--'}
													</Typography>
												</div>
											</div>
										</>
									</ModalConfirmation>
								)}
							</>
						)}
					</>
				</ModalConfirmation>
			)}
		</>
	);
}

function OfferDetailTable({ offer }: { offer: LoanOffer | CreditLineOffer }) {
	const rows = [
		{ label: 'Versión de política', value: offer.policyVersion },
		{
			label: 'Perfil',
			value: offer.profileTier ?? '--',
			valueColor: 'green_300',
		},
		{
			label: 'Uso comercial',
			value: offer.commercialUse ? 'Sí' : 'No',
			valueColor: 'green_300',
		},
		{
			label: 'Semanas',
			value: String(offer.duration),
			valueColor: 'green_300',
		},
		{
			label: 'Pago semanal toal',
			value: priceMXNTotal(offer.periodicPayment),
			valueColor: 'green_300',
		},
		{
			label: 'Pago semanal base',
			value: priceMXNTotal(offer.periodicBasePayment),
		},
		{
			label: 'Pago semanal del Seguro + GPS',
			value: priceMXNTotal(offer.periodicExtraPayment),
		},
		{
			label: 'Tasa semanal',
			value: `${(offer.periodicRate * 100).toFixed(2)}%`,
		},
		{
			label: 'Tasa con IVA',
			value: `${(offer.periodicRateWithTax * 100).toFixed(2)}%`,
		},
		{
			label: 'Ticket de la moto',
			value: priceMXNTotal(offer.ticketPrice),
			valueColor: 'green_300',
		},
		{
			label: 'Enganche',
			value: priceMXNTotal(offer.downPaymentAmount),
			valueColor: 'green_300',
		},
		{
			label: '% Enganche',
			value: `${(offer.downPaymentPct * 100).toFixed(1)}%`,
		},
		{
			label: 'Monto a financiar',
			value: priceMXNTotal(offer.financingAmount),
			valueColor: 'green_300',
		},
		{
			label: 'Total seguro',
			value: priceMXNTotal(offer.totalInsuranceAmount),
		},
		{ label: 'Total GPS', value: priceMXNTotal(offer.totalGpsAmount) },
		{
			label: 'Total Seguro + GPS',
			value: priceMXNTotal(offer.totalExtraAmount),
			valueColor: 'green_300',
		},
		...('monthlyIncome' in offer
			? [
					{
						label: 'Ingreso mensual',
						value: priceMXNTotal(offer.monthlyIncome),
					},
					{
						label: 'Cap. mensual máx.',
						value: priceMXNTotal(offer.maxMonthlyCapacity),
					},
					{
						label: 'Cap. periódica máx.',
						value: priceMXNTotal(offer.maxPeriodicCapacity),
					},
					{
						label: 'Cap. de pago máx.',
						value: priceMXNTotal(offer.maxPaymentCapacity),
					},
					{
						label: 'Cap. de financiamiento máx.',
						value: priceMXNTotal(offer.maxFinancingCapacity),
					},
			  ]
			: []),
		{ label: 'Moneda', value: offer.currency },
		{ label: 'Frecuencia de pago', value: offer.paymentFrequency },
		{
			label: 'Fecha de expiración',
			value: offer.expirationDate
				? momentLocal(offer.expirationDate).format('DD/MM/YYYY HH:mm')
				: '--',
		},
		{
			label: 'Fecha de creación',
			value: offer.createdAt
				? momentLocal(offer.createdAt).format('DD/MM/YYYY HH:mm')
				: '--',
		},
	];

	return (
		<>
			<Typography scale="large" weight="600" className="m_b_md">
				Detalle de la oferta
			</Typography>
			<DataTable data={rows} weightLabels={2} weightValues={2} />
		</>
	);
}

export function ValidateOfferAction() {
	const { financialForm, vehicle, initializeContext } =
		useContext(ozonerContext);
	const {
		validOffer,
		expiredOffer,
		offerExists,
		validDownPayment,
		validTicket,
		validDuration,
		validVehiclePurpose,
		validProvider,
		validMaxPaymentAmount,
		validMaxFinancingAmount,
		validPreOffer,
		validScoring,
	} = useValidOffer();

	const form = financialForm?.form as any;
	const isCreditLine = form?.type === FinancialFormTypeEnum.creditLine;
	const isPayJoyEmployeesWhitelist =
		form?.whitelistName === WhitelistNameEnum.payJoyEmployees;

	const [skus, setSkus] = useState<Sku[]>([]);
	const [validating, setValidating] = useState(false);
	const [assigning, setAssigning] = useState(false);
	const [openOfferModal, setOpenOfferModal] = useState(false);

	useEffect(() => {
		getSkus({
			status: SkuStatusEnum.ACTIVE,
			requirePricedVariant: true,
			limit: -1,
		})
			.then((result) => setSkus(result.data))
			.catch(() => {});
	}, []);

	const selectedSkuId = form?.sku as string | undefined;
	const selectedSkuVariantId = form?.skuVariant as string | undefined;
	const selectedSku = skus.find((s) => s._id === selectedSkuId);
	const selectedSkuVariant = selectedSku?.variants?.find(
		(v) => v._id === selectedSkuVariantId,
	);
	const ticketPrice = selectedSkuVariant?.price || selectedSku?.minPrice || 0;

	const downPayment: number | undefined = isCreditLine
		? form?.downPayment
		: form?.loanApplication?.downPayment;
	const loanDuration: number | undefined = isCreditLine
		? form?.loanDuration
		: form?.loanApplication?.loanDuration;

	const canValidate =
		validDownPayment !== undefined &&
		validTicket !== undefined &&
		validDuration !== undefined &&
		validVehiclePurpose !== undefined;

	const validateOffer = () => {
		if (!financialForm?.form) return;
		setValidating(true);

		const formId = financialForm.form._id;

		const calculateAndSave = isCreditLine
			? calculateAndSaveOffersForCreditLine
			: calculateAndSaveOffers;

		calculateAndSave(formId, {
			ticketPrice,
			downPaymentAmount: downPayment,
			loanDuration,
		})
			.then(() => {
				toasterNotification({
					msg: 'Oferta calculada y guardada correctamente',
					style: 'light',
					toasterType: toasterOptions.success,
				});
				initializeContext();
			})
			.catch((e) => {
				toasterNotification({
					msg: e?.message ?? 'No se pudo calcular una oferta válida',
					style: 'light',
					toasterType: toasterOptions.error,
				});
			})
			.finally(() => setValidating(false));
	};

	const offer = form?.offer as { downPaymentAmount?: number } | undefined;
	const requiredStatusForAssign = isCreditLine
		? FormStatus.pending
		: FormStatus.approved;
	const canAssignVehicle =
		vehicle === undefined &&
		validOffer === true &&
		form?.status === requiredStatusForAssign &&
		offer?.downPaymentAmount === 0;

	const handleAssignVehicle = () => {
		if (!financialForm?.form) return;
		setAssigning(true);
		assignVehicle(financialForm.form._id)
			.then(() => {
				toasterNotification({
					msg: 'Moto asignada y reservada correctamente',
					style: 'light',
					toasterType: toasterOptions.success,
				});
				initializeContext();
			})
			.catch((e) => {
				toasterNotification({
					msg: e?.message ?? 'No se pudo asignar la moto',
					style: 'light',
					toasterType: toasterOptions.error,
				});
			})
			.finally(() => setAssigning(false));
	};

	return (
		<div className="p_md border_b_solid border_1 border_neutral_400">
			<Typography
				scale="medium"
				weight="600"
				textColor="neutral_900"
				className="m_b_xs"
			>
				Oferta
			</Typography>
			<div className=" display_flex flex_col flex_gap_md">
				{/* Only Loan Application */}
				{!isCreditLine && validPreOffer === undefined && (
					<div className="display_flex flex_gap_xs flex_align_center">
						{validationItem(validationItemState.invalid, {
							[validationItemState.invalid]: 'Sin preoferta asignada',
						})}
					</div>
				)}
				{!isCreditLine && validPreOffer !== undefined && (
					<div className="display_flex flex_gap_xs flex_align_center">
						{validationItem(
							validPreOffer
								? validationItemState.valid
								: validationItemState.invalid,
							{
								[validationItemState.valid]: 'Preoferta valida',
								[validationItemState.invalid]: 'Preoferta rechazada o expirada',
							},
						)}
					</div>
				)}
				{!isCreditLine && validScoring === false && (
					<div className="display_flex flex_gap_xs flex_align_center">
						{validationItem(validationItemState.invalid, {
							[validationItemState.invalid]:
								'Perfil de la oferta y la preoferta no coinciden',
						})}
					</div>
				)}

				{/* Only PayJoy Employees */}
				{isPayJoyEmployeesWhitelist &&
					validMaxFinancingAmount === undefined && (
						<div className="display_flex flex_gap_xs flex_align_center">
							{validationItem(validationItemState.invalid, {
								[validationItemState.invalid]:
									'Financiamiento máximo sin asignar',
							})}
						</div>
					)}
				{isPayJoyEmployeesWhitelist &&
					validMaxFinancingAmount === false &&
					offerExists && (
						<div className="display_flex flex_gap_xs flex_align_center">
							{validationItem(validationItemState.invalid, {
								[validationItemState.invalid]:
									'Financiamiento de la oferta supera el máximo permitido',
							})}
						</div>
					)}

				{/* Only Credit Line */}
				{isCreditLine && validMaxPaymentAmount === undefined && (
					<div className="display_flex flex_gap_xs flex_align_center">
						{validationItem(validationItemState.invalid, {
							[validationItemState.invalid]: 'Pago semanal máximo sin asignar',
						})}
					</div>
				)}
				{isCreditLine && validMaxPaymentAmount === false && offerExists && (
					<div className="display_flex flex_gap_xs flex_align_center">
						{validationItem(validationItemState.invalid, {
							[validationItemState.invalid]:
								'Pago semanal de la oferta supera el máximo permitido',
						})}
					</div>
				)}

				{/* General */}
				{validTicket === undefined && (
					<div className="display_flex flex_gap_xs flex_align_center">
						{validationItem(validationItemState.invalid, {
							[validationItemState.invalid]: 'SKU sin asignar',
						})}
					</div>
				)}
				{validDownPayment === undefined && (
					<div className="display_flex flex_gap_xs flex_align_center">
						{validationItem(validationItemState.invalid, {
							[validationItemState.invalid]: 'Enganche sin asignar',
						})}
					</div>
				)}
				{validDuration === undefined && (
					<div className="display_flex flex_gap_xs flex_align_center">
						{validationItem(validationItemState.invalid, {
							[validationItemState.invalid]: 'Plazo sin asignar',
						})}
					</div>
				)}
				{validVehiclePurpose === undefined && (
					<div className="display_flex flex_gap_xs flex_align_center">
						{validationItem(validationItemState.invalid, {
							[validationItemState.invalid]: 'Uso del vehículo sin definir',
						})}
					</div>
				)}

				{/* Offer */}
				{!offerExists && (
					<div className="display_flex flex_gap_xs flex_align_center">
						{validationItem(validationItemState.invalid, {
							[validationItemState.invalid]: 'No se ha generado una oferta',
						})}
					</div>
				)}
				{offerExists && expiredOffer === true && (
					<div className="display_flex flex_gap_xs flex_align_center">
						{validationItem(validationItemState.invalid, {
							[validationItemState.invalid]: 'Oferta expirada',
						})}
					</div>
				)}
				{offerExists && validOffer === false && (
					<div className="display_flex flex_gap_xs flex_align_center">
						{validationItem(validationItemState.invalid, {
							[validationItemState.invalid]: 'Oferta sin validar',
						})}
					</div>
				)}
				{offerExists && validOffer && validProvider === undefined && (
					<div className="display_flex flex_gap_xs flex_align_center">
						{validationItem(validationItemState.invalid, {
							[validationItemState.invalid]: 'Proveedor sin asignar',
						})}
					</div>
				)}
				{!validOffer && (
					<Button
						scale="small"
						color="primary"
						className="w_100_per"
						onClick={validateOffer}
						disabled={!canValidate || validating}
					>
						{offerExists ? 'Validar oferta' : 'Generar oferta'}
					</Button>
				)}
				{validOffer && (
					<>
						<Button
							scale="small"
							color="secondary"
							className="w_100_per text_neutral_0"
							onClick={() => setOpenOfferModal(true)}
						>
							Ver oferta
						</Button>
						{canAssignVehicle && (
							<Button
								scale="small"
								color="primary"
								className="w_100_per"
								onClick={handleAssignVehicle}
								disabled={validProvider === undefined || assigning}
							>
								Asignar moto
							</Button>
						)}
					</>
				)}
				{openOfferModal && form?.offer && (
					<ModalConfirmation
						openConfirmation={openOfferModal}
						handleClose={() => setOpenOfferModal(false)}
						icon={<Calculator className="text_secondary_600" />}
						widthDesktop={55}
						actions={[
							{
								label: 'Cerrar',
								color: 'grey',
								action: () => setOpenOfferModal(false),
							},
						]}
					>
						<OfferDetailTable offer={form.offer} />
					</ModalConfirmation>
				)}
			</div>
		</div>
	);
}

export function GenericOpenpayLink({
	type,
	title: titleProp,
}: {
	type: PaymentLinkType;
	title?: string;
}) {
	const {
		setOpenVehicle,
		financialForm,
		paymentLinks,
		paymentLinksLoading,
		setPaymentLinksLoading,
		updatePaymentLinks,
	} = useContext(ozonerContext);
	const [openConfirmation, setOpenConfirmation] = useState(false);
	const [currentLink, setCurrentLink] = useState<PaymentLink | undefined>();
	const [amountInput, setAmountInput] = useState<number | undefined>();

	const requiresAmount = type === PaymentLinkType.PrincipalPayment;

	useEffect(() => {
		// Un pago a capital puede repetirse (un crédito puede recibir varios
		// abonos) — a diferencia del enganche, que es único por solicitud. Por
		// eso, para PrincipalPayment solo se toma como "link vigente" uno que
		// sigue esperando pago; uno ya completado no debe bloquear la
		// generación de un nuevo abono.
		setCurrentLink(
			paymentLinks.find(
				(ele) =>
					ele.paymentLinkType === type &&
					(isPaymentLinkAwaiting(ele.status) ||
						(requiresAmount ? false : isPaymentLinkCompleted(ele.status))),
			),
		);
	}, [paymentLinks, type, requiresAmount]);

	const generatePaymentLink = (method: PaymentMethodEnum) => {
		if (requiresAmount && (!amountInput || amountInput <= 0)) {
			toasterNotification({
				msg: 'Escribe un monto válido para el pago a capital',
				style: 'light',
				toasterType: toasterOptions.error,
			});
			return;
		}
		if (financialForm.form._id) {
			createPaymentLink(
				financialForm.form._id,
				type,
				method,
				requiresAmount ? amountInput : undefined,
			)
				.then((k) => {
					updatePaymentLinks();
				})
				.catch((err) => {
					if (
						err.statusCode === 400 &&
						err.message === 'Vehicle not available'
					) {
						setOpenVehicle(true);
						toasterNotification({
							msg: 'El vehículo ya no esta disponible',
							style: 'light',
							toasterType: toasterOptions.error,
						});
					} else {
						toasterNotification({
							msg: err.message,
							style: 'light',
							toasterType: toasterOptions.error,
						});
					}
				});
		}
	};

	const copyLink = () => {
		if (currentLink) {
			navigator.clipboard
				.writeText(currentLink.URL)
				.then(() => {
					toasterNotification({
						msg: 'Datos copiados correctamente',
						style: 'light',
						toasterType: toasterOptions.success,
					});
				})
				.catch((e) => {
					commonErrorHandler(e);
				});
		}
	};

	const cancelLink = () => {
		if (currentLink?._id) {
			setPaymentLinksLoading(true);
			cancelPaymentLink(currentLink._id)
				.then(() => {
					toasterNotification({
						msg: 'Link de pago cancelado, ya puedes generar uno nuevo',
						style: 'light',
						toasterType: toasterOptions.success,
					});
					updatePaymentLinks();
				})
				.catch((err) => {
					toasterNotification({
						msg: err.message ?? 'Ocurrio un error al cancelar el link de pago',
						style: 'light',
						toasterType: toasterOptions.error,
					});
				})
				.finally(() => {
					setPaymentLinksLoading(false);
					setOpenConfirmation(false);
				});
		}
	};

	const sendLinkByEmail = () => {
		if (currentLink?._id) {
			setPaymentLinksLoading(true);
			sendPaymentLinkEmail(currentLink._id)
				.then(() => {
					toasterNotification({
						msg: 'Correo enviado correctamente',
						style: 'light',
						toasterType: toasterOptions.success,
					});
				})
				.catch((err) => {
					toasterNotification({
						msg: err.message ?? 'Ocurrio un error al enviar el correo',
						style: 'light',
						toasterType: toasterOptions.error,
					});
				})
				.finally(() => {
					setPaymentLinksLoading(false);
				});
		}
	};
	return (
		<div
			id={`${type}-credito`}
			className="p_md border_b_solid border_1 border_neutral_400 display_flex flex_col flex_gap_xs"
		>
			<Typography scale="medium" weight="600" className="text_center">
				{titleProp ?? PaymentLinkTypeValues[type]}
			</Typography>

			{!currentLink ? (
				<div className="flex_center flex_col flex_gap_xs">
					{requiresAmount && (
						<Input
							type="number"
							onChange={(e: any) =>
								setAmountInput(
									e.target.value === ''
										? undefined
										: parseFloat(e.target.value),
								)
							}
							value={amountInput}
							title="Monto del pago a capital"
							icon={<Money />}
							name="principalPaymentAmount"
							placeholder="Escribe el monto a abonar"
							className="w_100_per"
						/>
					)}
					<Button
						icon={<Card />}
						scale="small"
						onClick={() => generatePaymentLink(PaymentMethodEnum.Digital)}
						disabled={paymentLinksLoading}
					>
						Generar Pago Digital
					</Button>
					<Button
						icon={<Money />}
						scale="small"
						onClick={() => generatePaymentLink(PaymentMethodEnum.Store)}
						disabled={paymentLinksLoading}
					>
						Generar Pago en Efectivo
					</Button>
					<Button
						icon={<Bank />}
						scale="small"
						onClick={() => generatePaymentLink(PaymentMethodEnum.BankAccount)}
						disabled={paymentLinksLoading}
					>
						Generar Pago SPEI
					</Button>
				</div>
			) : (
				<>
					<div className="flex_center flex_gap_xs">
						<Typography
							scale="small"
							weight="600"
							textColor={
								getPaymentLinkStatusDisplay(currentLink.status).color as any
							}
							className="text_no_break text_center"
						>
							{getPaymentLinkStatusDisplay(currentLink.status).title}
						</Typography>
						{isPaymentLinkCompleted(currentLink.status) ? (
							<Check className="text_green_300 dim_md" />
						) : (
							<Clock className="text_yellow_100 dim_md" />
						)}
					</div>
					<div className="display_flex flex_justify_between">
						<Typography scale="small" weight="600" textColor="neutral_900">
							Monto:
						</Typography>
						<Typography scale="small" weight="400" textColor="neutral_900">
							{priceFormatFinancial(currentLink.amount)}
						</Typography>
					</div>
					<div className="display_flex flex_justify_between">
						<Typography scale="small" weight="600" textColor="neutral_900">
							Metodo:
						</Typography>
						<Typography scale="small" weight="400" textColor="neutral_900">
							{PaymentLinkMethodsValues[currentLink.paymentMethod]}
						</Typography>
					</div>
					<div className="display_flex flex_justify_between">
						<Typography scale="small" weight="600" textColor="neutral_900">
							Fecha{' '}
							{isPaymentLinkCompleted(currentLink.status) ? 'Pago' : 'Creacion'}
							:
						</Typography>
						<Typography scale="small" weight="400" textColor="neutral_900">
							{momentLocal(
								isPaymentLinkCompleted(currentLink.status)
									? currentLink.paidAt
									: currentLink.createdAt,
							).format('DD/MM/YYYY HH:mm:ss')}
						</Typography>
					</div>
					{currentLink.paymentMethod === PaymentMethodEnum.BankAccount && (
						<>
							{currentLink.clabe && (
								<div className="display_flex flex_justify_between">
									<Typography
										scale="small"
										weight="600"
										textColor="neutral_900"
									>
										CLABE:
									</Typography>
									<Typography
										scale="small"
										weight="400"
										textColor="neutral_900"
									>
										{currentLink.clabe}
									</Typography>
								</div>
							)}
							{currentLink.bank && (
								<div className="display_flex flex_justify_between">
									<Typography
										scale="small"
										weight="600"
										textColor="neutral_900"
									>
										Banco:
									</Typography>
									<Typography
										scale="small"
										weight="400"
										textColor="neutral_900"
									>
										{currentLink.bank}
									</Typography>
								</div>
							)}
							{currentLink.reference && (
								<div className="display_flex flex_justify_between">
									<Typography
										scale="small"
										weight="600"
										textColor="neutral_900"
									>
										Referencia:
									</Typography>
									<Typography
										scale="small"
										weight="400"
										textColor="neutral_900"
									>
										{currentLink.reference}
									</Typography>
								</div>
							)}
						</>
					)}
					{!isPaymentLinkCompleted(currentLink.status) ? (
						<>
							<Button
								scale="small"
								color="secondary"
								onClick={() => copyLink()}
								disabled={paymentLinksLoading}
							>
								Copiar Link
							</Button>
							<Button
								scale="small"
								color="primary"
								onClick={() => updatePaymentLinks()}
								disabled={paymentLinksLoading}
							>
								Actualizar Estado
							</Button>
							<Button
								icon={<Mail />}
								scale="small"
								color="secondary"
								onClick={() => sendLinkByEmail()}
								disabled={paymentLinksLoading}
							>
								Enviar por Correo
							</Button>
							<Button
								icon={<CloseCircle />}
								scale="small"
								color="grey"
								onClick={() => setOpenConfirmation(true)}
								disabled={paymentLinksLoading}
							>
								Cancelar Link
							</Button>
						</>
					) : null}
				</>
			)}
			{openConfirmation && (
				<ModalConfirmation
					openConfirmation={openConfirmation}
					handleClose={() => setOpenConfirmation(false)}
					icon={<CloseCircle />}
					isConfirmationLoading={paymentLinksLoading}
					widthDesktop={30}
					actions={[
						{
							label: 'Volver',
							color: 'grey',
							action: () => setOpenConfirmation(false),
						},
						{
							label: 'Cancelar Link',
							color: 'primary',
							action: cancelLink,
						},
					]}
				>
					<>
						<Typography scale="large" weight="600" className="m_b_md">
							¿Cancelar este link de pago?
						</Typography>
						<Typography scale="medium" weight="400" className="text_center">
							Podrás generar un nuevo link con otro método de pago después de
							cancelar este.
						</Typography>
					</>
				</ModalConfirmation>
			)}
		</div>
	);
}

export function CreditLineDownPayment() {
	const { financialForm, updateFormData } = useContext(ozonerContext);

	const [open, setOpen] = useState(false);
	const [amount, setAmount] = useState<number | undefined>();

	const save = () => {
		if (financialForm?.form) {
			updateCreditLineFinancialForm(financialForm.form._id, {
				downPayment: amount,
			})
				.then((newForm) => {
					toasterNotification({
						msg: 'Enganche actualizado',
						style: 'light',
						toasterType: toasterOptions.success,
					});
					updateFormData(newForm);
					setOpen(false);
				})
				.catch(commonErrorHandler);
		}
	};

	return (
		<>
			<div className="p_md border_b_solid border_1 border_neutral_400 pos_relative">
				<Typography scale="medium" weight="400" textColor="neutral_800">
					Enganche Credit Line
				</Typography>
				<Typography scale="large" weight="600" textColor="neutral_800">
					{(financialForm?.form as any)?.downPayment !== undefined
						? priceMXNTotal((financialForm.form as any).downPayment, 2)
						: '      -- MXN'}
				</Typography>
				<Button
					onClick={() => {
						setAmount((financialForm?.form as any)?.downPayment);
						setOpen(true);
					}}
					variant="icon"
					icon={<Edit />}
					subvariant="edit"
					scale="small"
					className="edit-button-deposit"
				/>
			</div>
			{open && (
				<ModalConfirmation
					openConfirmation={open}
					handleClose={() => setOpen(false)}
					icon={<Edit />}
					isConfirmationLoading={false}
					widthDesktop={30}
					actions={[
						{
							label: 'Cancelar',
							color: 'grey',
							action: () => setOpen(false),
						},
						{
							label: 'Guardar',
							color: 'primary',
							action: save,
						},
					]}
				>
					<>
						<Typography scale="large" weight="600" className="m_b_md">
							¿Modificar Enganche Credit Line?
						</Typography>
						<Input
							type="number"
							onChange={(e: any) => setAmount(parseFloat(e.target.value))}
							value={amount}
							title="Monto del enganche"
							icon={<Money />}
							name="downPayment"
							placeholder="Escribe el monto del enganche aquí"
						/>
					</>
				</ModalConfirmation>
			)}
		</>
	);
}

export function LoanIdPayjoyInput() {
	const { financialForm, updateFormData } = useContext(ozonerContext);

	const [open, setOpen] = React.useState(false);
	const [value, setValue] = React.useState('');

	const save = () => {
		if (financialForm?.form) {
			updateCreditLineFinancialForm(financialForm.form._id, {
				loanIdPayjoy: value,
			})
				.then((newForm) => {
					toasterNotification({
						msg: 'ID PayJoy actualizado',
						style: 'light',
						toasterType: toasterOptions.success,
					});
					updateFormData(newForm);
					setOpen(false);
				})
				.catch(commonErrorHandler);
		}
	};

	return (
		<>
			<div className="p_md border_b_solid border_1 border_neutral_400 pos_relative">
				<Typography scale="medium" weight="400" textColor="neutral_800">
					ID Crédito PayJoy
				</Typography>
				<Typography scale="large" weight="600" textColor="neutral_800">
					{(financialForm?.form as any)?.loanIdPayjoy ?? '--'}
				</Typography>
				<Button
					onClick={() => {
						setValue((financialForm?.form as any)?.loanIdPayjoy ?? '');
						setOpen(true);
					}}
					variant="icon"
					icon={<Edit />}
					subvariant="edit"
					scale="small"
					className="edit-button-deposit"
				/>
			</div>
			{open && (
				<ModalConfirmation
					openConfirmation={open}
					handleClose={() => setOpen(false)}
					icon={<Edit />}
					isConfirmationLoading={false}
					widthDesktop={30}
					actions={[
						{
							label: 'Cancelar',
							color: 'grey',
							action: () => setOpen(false),
						},
						{
							label: 'Guardar',
							color: 'primary',
							action: save,
						},
					]}
				>
					<>
						<Typography scale="large" weight="600" className="m_b_md">
							¿Modificar ID Crédito PayJoy?
						</Typography>
						<Input
							title="ID Crédito PayJoy"
							name="loanIdPayjoy"
							type="text"
							placeholder="Ej: PJ-2024-000123"
							value={value}
							onChange={(e: any) => setValue(e.target.value)}
						/>
					</>
				</ModalConfirmation>
			)}
		</>
	);
}

export function MaxFinancingAmountInput() {
	const { financialForm, updateFormData } = useContext(ozonerContext);

	const [open, setOpen] = React.useState(false);
	const [amount, setAmount] = React.useState<number | undefined>();

	const save = () => {
		if (financialForm?.form) {
			updateCreditLineFinancialForm(financialForm.form._id, {
				maxFinancingAmount: amount,
			})
				.then((newForm) => {
					toasterNotification({
						msg: 'Monto máximo a financiar actualizado',
						style: 'light',
						toasterType: toasterOptions.success,
					});
					updateFormData(newForm);
					setOpen(false);
				})
				.catch(commonErrorHandler);
		}
	};

	return (
		<>
			<div className="p_md border_b_solid border_1 border_neutral_400 pos_relative">
				<Typography scale="medium" weight="400" textColor="neutral_800">
					Monto máximo a financiar
				</Typography>
				<Typography scale="large" weight="600" textColor="neutral_800">
					{(financialForm?.form as any)?.maxFinancingAmount !== undefined
						? priceMXNTotal((financialForm.form as any).maxFinancingAmount, 2)
						: '      -- MXN'}
				</Typography>
				<Button
					onClick={() => {
						setAmount((financialForm?.form as any)?.maxFinancingAmount);
						setOpen(true);
					}}
					variant="icon"
					icon={<Edit />}
					subvariant="edit"
					scale="small"
					className="edit-button-deposit"
				/>
			</div>
			{open && (
				<ModalConfirmation
					openConfirmation={open}
					handleClose={() => setOpen(false)}
					icon={<Edit />}
					isConfirmationLoading={false}
					widthDesktop={30}
					actions={[
						{
							label: 'Cancelar',
							color: 'grey',
							action: () => setOpen(false),
						},
						{
							label: 'Guardar',
							color: 'primary',
							action: save,
						},
					]}
				>
					<>
						<Typography scale="large" weight="600" className="m_b_md">
							¿Modificar monto máximo a financiar?
						</Typography>
						<Input
							type="number"
							onChange={(e: any) => setAmount(parseFloat(e.target.value))}
							value={amount}
							title="Monto máximo a financiar"
							icon={<Money />}
							name="maxFinancingAmount"
							placeholder="Escribe el monto máximo a financiar aquí"
						/>
					</>
				</ModalConfirmation>
			)}
		</>
	);
}

export function MaxPeriodicPaymentInput() {
	const { financialForm, updateFormData } = useContext(ozonerContext);

	const [open, setOpen] = React.useState(false);
	const [amount, setAmount] = React.useState<number | undefined>();

	const save = () => {
		if (financialForm?.form) {
			updateCreditLineFinancialForm(financialForm.form._id, {
				maxPeriodicPayment: amount,
			})
				.then((newForm) => {
					toasterNotification({
						msg: 'Pago semanal máximo actualizado',
						style: 'light',
						toasterType: toasterOptions.success,
					});
					updateFormData(newForm);
					setOpen(false);
				})
				.catch(commonErrorHandler);
		}
	};

	return (
		<>
			<div className="p_md border_b_solid border_1 border_neutral_400 pos_relative">
				<Typography scale="medium" weight="400" textColor="neutral_800">
					Pago semanal máximo
				</Typography>
				<Typography scale="large" weight="600" textColor="neutral_800">
					{(financialForm?.form as any)?.maxPeriodicPayment !== undefined
						? priceMXNTotal((financialForm.form as any).maxPeriodicPayment, 2)
						: '      -- MXN'}
				</Typography>
				<Button
					onClick={() => {
						setAmount((financialForm?.form as any)?.maxPeriodicPayment);
						setOpen(true);
					}}
					variant="icon"
					icon={<Edit />}
					subvariant="edit"
					scale="small"
					className="edit-button-deposit"
				/>
			</div>
			{open && (
				<ModalConfirmation
					openConfirmation={open}
					handleClose={() => setOpen(false)}
					icon={<Edit />}
					isConfirmationLoading={false}
					widthDesktop={30}
					actions={[
						{
							label: 'Cancelar',
							color: 'grey',
							action: () => setOpen(false),
						},
						{
							label: 'Guardar',
							color: 'primary',
							action: save,
						},
					]}
				>
					<>
						<Typography scale="large" weight="600" className="m_b_md">
							¿Modificar pago semanal máximo?
						</Typography>
						<Input
							type="number"
							onChange={(e: any) => setAmount(parseFloat(e.target.value))}
							value={amount}
							title="Pago semanal máximo"
							icon={<Money />}
							name="maxPeriodicPayment"
							placeholder="Escribe el pago semanal máximo aquí"
						/>
					</>
				</ModalConfirmation>
			)}
		</>
	);
}
